'use client';

import { useSearchParams } from 'next/navigation';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import {
	CAREER_OFFER_ATTRIBUTION_COOKIE,
	CAREER_OFFER_ATTRIBUTION_DAYS,
	extractCareerOfferUtm,
	updateCareerOfferAttribution,
	type CareerOfferAttribution,
} from '../model/attribution';

const CareerOfferAttributionContext =
	createContext<CareerOfferAttribution | null>(null);

const readAttributionCookie = (): CareerOfferAttribution | null => {
	if (typeof document === 'undefined') return null;

	const prefix = `${CAREER_OFFER_ATTRIBUTION_COOKIE}=`;
	const rawValue = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(prefix))
		?.slice(prefix.length);
	if (!rawValue) return null;

	try {
		const value = JSON.parse(decodeURIComponent(rawValue));
		if (
			typeof value === 'object' &&
			value !== null &&
			typeof value.firstTouch === 'object' &&
			typeof value.lastTouch === 'object'
		) {
			return value as CareerOfferAttribution;
		}
	} catch {
		// Ignore malformed client cookies and start a fresh attribution chain.
	}
	return null;
};

const writeAttributionCookie = (attribution: CareerOfferAttribution) => {
	const expires = new Date(
		Date.now() + CAREER_OFFER_ATTRIBUTION_DAYS * 86_400_000,
	).toUTCString();
	document.cookie = `${CAREER_OFFER_ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(attribution))}; expires=${expires}; path=/; SameSite=Lax`;
};

const resolveAttribution = (
	searchParams: URLSearchParams,
	capturedAt: string,
) =>
	updateCareerOfferAttribution(
		readAttributionCookie(),
		extractCareerOfferUtm(searchParams),
		capturedAt,
	);

export function CareerOfferAttributionProvider({
	children,
}: React.PropsWithChildren) {
	const searchParams = useSearchParams();
	const capturedAt = useRef(new Date().toISOString());
	const query = searchParams.toString();
	const [attribution, setAttribution] = useState<CareerOfferAttribution | null>(
		() => resolveAttribution(new URLSearchParams(query), capturedAt.current),
	);

	useEffect(() => {
		const nextAttribution = resolveAttribution(
			new URLSearchParams(query),
			capturedAt.current,
		);
		setAttribution(nextAttribution);
		if (nextAttribution) writeAttributionCookie(nextAttribution);
	}, [query]);

	return (
		<CareerOfferAttributionContext.Provider value={attribution}>
			{children}
		</CareerOfferAttributionContext.Provider>
	);
}

export const useCareerOfferAttribution = () =>
	useContext(CareerOfferAttributionContext);
